<template>
  <div class="standard-table-container">
    <div class="table-container bg-white border-radius-4 overflow-hidden">
      <el-table
        ref="table"
        :data="tableData"
        v-loading="loading"
        :key="tableKey"
        :height="tableHeight"
        style="width: 100%"
        stripe
        border
        header-row-class-name="header-row-class-name"
        @selection-change="handleSelectionChange"
        @header-dragend="onHeaderDragend"
        highlight-current-row
      >
        <!-- 选择框列 -->
        <el-table-column 
          v-if="showSelection" 
          type="selection" 
          width="55" 
          fixed="left"
          align="center"
        ></el-table-column>
        
        <!-- 序号列 -->
        <el-table-column 
          v-if="showIndex" 
          label="序号" 
          :width="indexWidth" 
          fixed="left"
          align="center"
        >
          <template slot-scope="scope">
            {{ getIndexNumber(scope.$index) }}
          </template>
        </el-table-column>
        
        <!-- 数据列 -->
        <el-table-column 
          v-for="col in renderColumns" 
          :key="(col.prop || col.label) + '-' + (col.fixed || 'none')" 
          :prop="col.prop" 
          :label="col.label" 
          :min-width="col.minWidth || 120" 
          :resizable="true" 
          :fixed="col.fixed" 
          :type="col.type"
          :show-overflow-tooltip="true"
          :cell-class-name="col.cellClassName"
          align="center"
        >
          <template slot-scope="scope">
            <span v-if="scope.row[col.prop] && scope.row[col.prop] !== ''" 
                  class="table-cell-content"
                  :class="getCellClass(col, scope.row)">
              {{ scope.row[col.prop] }}
            </span>
            <span v-else class="empty-cell">--</span>
          </template>
        </el-table-column>
        
        <!-- 操作列 -->
        <el-table-column 
          v-if="showActions && actionColumns.length > 0"
          label="操作" 
          :fixed="actionFixed" 
          :width="actionWidth"
          class-name="operation-column"
          align="center"
        >
          <template slot-scope="scope">
            <!-- 前2个操作按钮直接显示 -->
            <el-button 
              v-for="action in visibleActions" 
              :key="action.key"
              :size="action.size || 'mini'" 
              :type="action.type || 'text'" 
              :disabled="action.disabled && action.disabled(scope.row)"
              @click="handleAction(action.key, scope.row, scope.$index)"
            >
              {{ action.label }}
            </el-button>
            
            <!-- 第3个及以后的操作显示为更多下拉菜单 -->
            <el-dropdown 
              v-if="moreActions.length > 0"
              @command="(command) => handleAction(command, scope.row, scope.$index)"
              trigger="click"
            >
              <el-button size="mini" type="text">
                更多<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item 
                  class="operation-item"
                  v-for="action in moreActions" 
                  :key="action.key"
                  :command="action.key"
                  :disabled="action.disabled && action.disabled(scope.row)"
                >
                  {{ action.label }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
        <template slot="empty">
          <div class="flex items-center justify-center table-empty">
            <img src="@/assets/no_data.png" alt="暂无数据" class="w-160 h-160">
          </div>
        </template>
      </el-table>
    </div>
  </div>
</template>

<script>
import tableScrollMixin from '@/mixins/tableScrollMixin'
export default {
  name: 'StandardTable',
  mixins: [tableScrollMixin],
  props: {
    // 表格数据
    tableData: {
      type: Array,
      default: () => []
    },
    // 加载状态
    loading: {
      type: Boolean,
      default: false
    },
    // 列配置
    columns: {
      type: Array,
      default: () => []
    },
    // 是否显示选择框
    showSelection: {
      type: Boolean,
      default: true
    },
    // 是否显示序号
    showIndex: {
      type: Boolean,
      default: true
    },
    // 是否显示操作列
    showActions: {
      type: Boolean,
      default: true
    },
    // 操作列配置
    actionColumns: {
      type: Array,
      default: () => []
    },
    // 操作列固定位置
    actionFixed: {
      type: String,
      default: 'right'
    },
    // 操作列宽度
    actionWidth: {
      type: [String, Number],
      default: '160'
    },
    // 序号列宽度（可自定义）
    indexWidth: {
      type: [String, Number],
      default: 60
    },
    // 表格固定高度（超出出现纵向滚动条）
    tableHeight: {
      type: [String, Number],
      default: null
    },
    // 当前页码（用于计算序号）
    currentPage: {
      type: Number,
      default: 1
    },
    // 每页大小（用于计算序号）
    pageSize: {
      type: Number,
      default: 50
    },
    // 操作列权限映射
    actionPermissions: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      renderColumns: [],
      tableKey: 0,
    }
  },
  computed: {
    // 显示的操作按钮（最多显示3个，如果超过3个则显示前2个）
    visibleActions() {
      const filteredActions = this.filterActionsByPermission(this.actionColumns)
      if (filteredActions.length <= 3) {
        return filteredActions
      }
      return filteredActions.slice(0, 2)
    },
    // 更多操作按钮（当操作按钮超过3个时，第3个及以后的所有按钮）
    moreActions() {
      const filteredActions = this.filterActionsByPermission(this.actionColumns)
      if (filteredActions.length <= 3) {
        return []
      }
      return filteredActions.slice(2)
    },
  },
  watch: {
    columns: {
      handler(val) {
        this.applyColumns(val)
      },
      immediate: true,
      deep: true
    },
    tableData: {
      handler() {
        // 数据变化时重新计算高度
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      },
      deep: true
    }
  },
  mounted() {
  },
  beforeDestroy() {
    // 清理事件监听
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
  },
  methods: {
    // 根据权限过滤操作按钮
    filterActionsByPermission(actions) {
      return actions.filter(action => {
        // 优先使用 actionColumns 中的 permission 属性
        if (action.permission) {
          return this.$permission && this.$permission(action.permission)
        }
        
        // 如果没有 permission 属性，则使用 actionPermissions 映射
        if (this.actionPermissions && this.actionPermissions[action.key]) {
          const permission = this.actionPermissions[action.key]
          // 如果权限字符串为空，则显示
          if (!permission) {
            return true
          }
          return this.$permission && this.$permission(permission)
        }
        
        // 如果都没有设置权限，则显示
        return true
      })
    },
    // 获取单元格类名
    getCellClass(col, row) {
      if (col.prop === 'status') {
        return row.status === '有效' ? 'status-valid' : 'status-invalid'
      }
      return ''
    },
    // 应用列配置
    applyColumns(cols) {
      // 扁平可见列，按固定列顺序排列
      const out = []
      const walk = arr => (arr || []).forEach(n => {
        if (n.children && n.children.length) {
          walk(n.children)
        } else if (n.visible !== false) {
          out.push({ 
            label: n.label, 
            prop: n.prop, 
            width: n.width, 
            minWidth: n.minWidth,
            fixed: n.fixed || null,
            type: n.type || null
          })
        }
      })
      walk(cols)
      
      // 按固定列顺序重新排列：左固定 -> 普通 -> 右固定
      const leftFixed = out.filter(c => c.fixed === 'left')
      const normal = out.filter(c => !c.fixed)
      const rightFixed = out.filter(c => c.fixed === 'right')
      const ordered = [...leftFixed, ...normal, ...rightFixed]
      
      this.$set(this, 'renderColumns', ordered)
      this.tableKey++
    },
    
    // 列宽度调整
    onHeaderDragend(newWidth, oldWidth, column) {
      const prop = column && column.property
      if (!prop) return
      const idx = this.columns.findIndex(c => c.prop === prop)
      if (idx !== -1) {
        // 对于数据列，保存为 minWidth 而不是 width
        this.$set(this.columns[idx], 'minWidth', newWidth)
        this.applyColumns(this.columns)
        this.$emit('column-resize', { prop, newWidth, oldWidth })
      }
    },
    
    // 选择变化
    handleSelectionChange(selection) {
      this.$emit('selection-change', selection)
    },
    
    // 操作按钮点击
    handleAction(actionKey, row, index) {
      // 点击操作按钮时设置当前行高亮
      this.$refs.table.setCurrentRow(row)
      this.$emit('action-click', { actionKey, row, index })
    },
    

    // 计算序号（支持分页累加）
    getIndexNumber(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    

  }
}
</script>

<style scoped>
</style>
